/**
 * CEO Command Center - Main Application
 * 
 * Root application component that:
 * - Initializes stores and fetches data
 * - Handles routing between views
 * - Manages global UI state (modals, toasts, chat)
 * - Provides error boundary
 * 
 * @module App
 * @version 10.3.0
 */

import React, { useEffect, useCallback, Suspense, lazy, useState } from 'react';
import {
  useUIStore,
  useAuthStore,
  useSignalCounts,
  useSyncStore,
  useDataStoreSQLite,
} from './stores';
import { useConnectionStore } from './stores/connectionStore';
import { useInitiativeStore } from './stores/initiativeStore';
import { AppLayout } from './components/layout';
import { ErrorBoundary, LoadingState, ConnectionErrorBanner, SyncSettingsPanel } from './components/common';
import { ToastProvider } from './components/ui';
import { ChatSidebar } from './components/features/chat';
import { TaskEditor } from './components/features/tasks';
import { InitiativeModal } from './components/features/initiatives';
import { DomainEditor } from './components/features/domains';
import { useDomainStore } from './stores/domainStore';
import { log } from './services/logger';
import type { ViewId } from './types';
import type { AICommand } from './services/api';

// ============================================
// LAZY LOADED VIEWS
// ============================================

const CommandCenterView = lazy(() => import('./views/CommandCenter'));
const DomainView = lazy(() => import('./views/DomainView'));
const GrowthView = lazy(() => import('./views/GrowthView'));
const PipelineView = lazy(() => import('./views/PipelineView'));
const BoardView = lazy(() => import('./views/BoardView'));
const FundraisingView = lazy(() => import('./views/FundraisingView'));
const FinanceView = lazy(() => import('./views/FinanceView'));
const MAUView = lazy(() => import('./views/MAUView'));
const InitiativeView = lazy(() => import('./views/InitiativeView'));
const KnowledgeView = lazy(() => import('./views/KnowledgeView'));
const SettingsView = lazy(() => import('./views/SettingsView'));
const TestView = lazy(() => import('./views/TestView'));
const MigrationView = lazy(() => import('./views/MigrationView'));

// ============================================
// LOGIN SCREEN
// ============================================

const LoginScreen: React.FC<{ onLogin: (name: string) => void }> = ({ onLogin }) => {
  const [name, setName] = React.useState('');
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim()) {
      onLogin(name.trim());
    }
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-800">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="text-5xl mb-4">🎯</div>
          <h1 className="text-2xl font-bold text-gray-900">CEO Command Center</h1>
          <p className="text-gray-500 mt-2">Executive Dashboard • Powered by Strives</p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Your Name
            </label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Enter your name"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              autoFocus
            />
          </div>
          <button
            type="submit"
            disabled={!name.trim()}
            className="w-full py-3 bg-primary-500 text-white font-semibold rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Enter Dashboard
          </button>
        </form>
        
        <p className="text-xs text-gray-400 text-center mt-6">
          v11.0.0 • Powered by Strives
        </p>
      </div>
    </div>
  );
};

// ============================================
// MAIN APP
// ============================================

const App: React.FC = () => {
  // Stores
  const { user, login, logout } = useAuthStore();
  const {
    currentView,
    setView,
    chatOpen,
    toggleChat,
    taskEditorOpen,
    taskEditorTask,
    openTaskEditor,
    closeTaskEditor,
    showToast,
  } = useUIStore();

  // Sync store (v11)
  const { init: initSync, initialized: syncInitialized } = useSyncStore();

  // v11: SQLite is now the only backend - Airtable has been deprecated
  const dataStore = useDataStoreSQLite();

  const {
    tasks: rawTasks,
    deals: rawDeals,
    clients: rawClients,
    owners: rawOwners,
    cashflow: rawCashflow,
    signals: rawSignals,
    loading,
    error,
    fetchAll,
    createTask,
    updateTask,
    deleteTask,
    toggleTaskStatus,
    toggleTaskSignal,
    createDeal,
    updateDeal,
    deleteDeal,
    createOwner,
    clearError,
  } = dataStore;
  
  // Ensure arrays are always arrays (safety for corrupted localStorage)
  const tasks = Array.isArray(rawTasks) ? rawTasks : [];
  const deals = Array.isArray(rawDeals) ? rawDeals : [];
  const clients = Array.isArray(rawClients) ? rawClients : [];
  const owners = Array.isArray(rawOwners) ? rawOwners : [];
  const cashflow = Array.isArray(rawCashflow) ? rawCashflow : [];
  const signals = Array.isArray(rawSignals) ? rawSignals : [];
  
  const { database: databaseConnection } = useConnectionStore();
  const { createInitiative, selectInitiative, selectedInitiativeId, deleteInitiative } = useInitiativeStore();
  const { addDomain, deleteDomain } = useDomainStore();
  const signalCounts = useSignalCounts();
  
  // Initiative modal state
  const [initiativeModalOpen, setInitiativeModalOpen] = useState(false);

  // Domain modal state
  const [domainModalOpen, setDomainModalOpen] = useState(false);

  // Sync settings modal state (for P2P setup)
  const [syncSettingsOpen, setSyncSettingsOpen] = useState(false);
  
  // Initialize sync store (v11 SQLite)
  useEffect(() => {
    if (user && !syncInitialized) {
      log.info('App', 'Initializing sync store (SQLite)');
      initSync().catch((err) => {
        log.warn('App', 'Sync store init failed, will use Airtable', { error: err.message });
      });
    }
  }, [user, syncInitialized, initSync]);

  // Fetch data on login - v11 uses SQLite only
  // IMPORTANT: Only fetch after sync store is initialized (database ready)
  useEffect(() => {
    if (user && syncInitialized) {
      log.info('App', 'Sync initialized, fetching local SQLite data');
      fetchAll();
    }
  }, [user, syncInitialized, fetchAll]);
  
  // Handle navigation
  const handleNavigate = useCallback((view: ViewId) => {
    log.action('Navigate', { from: currentView, to: view });
    // If navigating away from initiative, clear selection
    if (view !== 'initiative' && view !== 'aia') {
      selectInitiative(null);
    }
    setView(view);
  }, [setView, selectInitiative, currentView]);
  
  // Handle initiative selection from sidebar
  const handleSelectInitiative = useCallback((id: string) => {
    selectInitiative(id);
    setView('initiative');
  }, [selectInitiative, setView]);
  
  // Handle adding new initiative
  const handleAddInitiative = useCallback(() => {
    setInitiativeModalOpen(true);
  }, []);
  
  // Handle adding new domain
  const handleAddDomain = useCallback(() => {
    setDomainModalOpen(true);
  }, []);
  
  // Handle deleting a domain
  const handleDeleteDomain = useCallback((id: string) => {
    deleteDomain(id);
    showToast('Domain deleted', 'success');
    // Navigate to command center if currently viewing deleted domain
    if (currentView === id.toLowerCase()) {
      setView('command');
    }
  }, [deleteDomain, showToast, currentView, setView]);
  
  // Handle deleting an initiative
  const handleDeleteInitiative = useCallback((id: string) => {
    deleteInitiative(id);
    showToast('Initiative deleted', 'success');
    // Navigate to command center if currently viewing deleted initiative
    if (selectedInitiativeId === id) {
      selectInitiative(null);
      setView('command');
    }
  }, [deleteInitiative, showToast, selectedInitiativeId, selectInitiative, setView]);
  
  // Handle global actions (from command palette, etc.)
  const handleAction = useCallback((action: string) => {
    switch (action) {
      case 'new-task':
        openTaskEditor();
        break;
      case 'new-deal':
        // Would open deal editor
        break;
      case 'ai-chat':
        toggleChat();
        break;
      case 'refresh':
        fetchAll();
        showToast('Data refreshed', 'success');
        break;
    }
  }, [openTaskEditor, toggleChat, fetchAll, showToast]);
  
  // Handle AI commands
  const handleAICommand = useCallback(async (command: AICommand) => {
    try {
      switch (command.action) {
        case 'add_task':
          await createTask(command.data as Parameters<typeof createTask>[0]);
          showToast('Task created', 'success');
          break;
        case 'update_task':
          const { id, ...updates } = command.data as { id: string } & Parameters<typeof updateTask>[1];
          await updateTask(id, updates);
          showToast('Task updated', 'success');
          break;
        case 'mark_signal':
          await toggleTaskSignal(command.data.id as string);
          showToast('Signal toggled', 'success');
          break;
        default:
          console.warn('Unknown AI command:', command.action);
      }
    } catch (e) {
      console.error('Failed to execute AI command:', e);
      showToast('Failed to execute command', 'error');
    }
  }, [createTask, updateTask, toggleTaskSignal, showToast]);
  
  // Handle task save from editor
  const handleTaskSave = useCallback(async (taskData: Parameters<typeof createTask>[0]) => {
    // Auto-create owner if it's a new name
    if (taskData.owner && taskData.owner.trim()) {
      const ownerExists = owners.some(
        o => o.name.toLowerCase() === taskData.owner!.toLowerCase()
      );
      if (!ownerExists) {
        try {
          await createOwner({ name: taskData.owner.trim() });
          log.info('Owners', 'New owner auto-created', { name: taskData.owner });
        } catch (err) {
          log.warn('Owners', 'Failed to auto-create owner', { name: taskData.owner, error: err });
          // Continue with task save even if owner creation fails
        }
      }
    }

    if (taskEditorTask) {
      log.action('Task Update', { taskId: taskEditorTask.id, title: taskData.title });
      await updateTask(taskEditorTask.id, taskData);
      showToast('Task updated', 'success');
      log.info('Tasks', 'Task updated successfully', { taskId: taskEditorTask.id });
    } else {
      log.action('Task Create', { title: taskData.title, domain: taskData.domain });
      await createTask(taskData);
      showToast('Task created', 'success');
      log.info('Tasks', 'Task created successfully', { title: taskData.title });
    }
    closeTaskEditor();
  }, [taskEditorTask, createTask, updateTask, closeTaskEditor, showToast, owners, createOwner]);
  
  // Render current view
  const renderView = () => {
    // Common props for domain views
    const domainViewProps = {
      tasks,
      owners,
      loading,
      onTaskCreate: createTask,
      onTaskUpdate: updateTask,
      onTaskDelete: deleteTask,
      onTaskToggle: toggleTaskStatus,
      onTaskSignal: toggleTaskSignal,
    };
    
    switch (currentView) {
      case 'command':
        return (
          <CommandCenterView
            tasks={tasks}
            deals={deals}
            clients={clients}
            cashflow={cashflow}
            signals={signals}
            loading={loading}
            onTaskToggle={toggleTaskStatus}
            onTaskEdit={openTaskEditor}
            onTaskSignal={toggleTaskSignal}
            onNewTask={() => openTaskEditor()}
            onNewDeal={() => handleAction('new-deal')}
            onViewAll={(type) => {
              if (type === 'tasks') setView('emg');
              if (type === 'deals') setView('growth');
            }}
          />
        );
      
      case 'emg':
        return <DomainView domain="EMG" {...domainViewProps} />;
      case 'product':
        return <DomainView domain="PRODUCT" {...domainViewProps} />;
      case 'growth':
        // D6: Growth is now task-focused like EMG/Product
        // Pipeline/deals moved to STRATEGIC > Pipeline Overview
        return <DomainView domain="GROWTH" {...domainViewProps} />;
      case 'operations':
        return <DomainView domain="OPERATIONS" {...domainViewProps} />;
      case 'finance':
        // Finance now uses same DomainView structure as other business domains
        return <DomainView domain="FINANCE" {...domainViewProps} />;
      case 'cashflow':
        // Cash Flow analytics view (separate from Finance domain tasks)
        return (
          <FinanceView
            cashflow={cashflow}
            deals={deals}
            clients={clients}
            loading={loading}
          />
        );
      case 'aia':
        return <DomainView domain="AIA" {...domainViewProps} />;
      case 'mau':
        return (
          <MAUView
            tasks={tasks}
            loading={loading}
            onTaskToggle={toggleTaskStatus}
          />
        );
      case 'fundraising':
        return (
          <FundraisingView
            tasks={tasks}
            investors={useDataStoreSQLite.getState().investors}
            loading={loading}
            onInvestorAdd={useDataStoreSQLite.getState().addInvestor}
            onInvestorUpdate={useDataStoreSQLite.getState().updateInvestor}
            onInvestorDelete={useDataStoreSQLite.getState().deleteInvestor}
            onTaskToggle={toggleTaskStatus}
          />
        );
      case 'board':
        return (
          <BoardView
            tasks={tasks}
            deals={deals}
            clients={clients}
            cashflow={cashflow}
            loading={loading}
            onTaskToggle={toggleTaskStatus}
            onTaskEdit={openTaskEditor}
          />
        );
      
      // Pipeline view - Kanban board for deals
      case 'pipeline':
        return (
          <PipelineView
            deals={deals}
            owners={owners}
            loading={loading}
            onDealCreate={createDeal}
            onDealUpdate={updateDeal}
            onDealDelete={deleteDeal}
          />
        );
      
      // Initiative view
      case 'initiative':
        return selectedInitiativeId ? (
          <InitiativeView
            initiativeId={selectedInitiativeId}
            owners={owners}
            loading={loading}
          />
        ) : (
          <CommandCenterView {...{
            tasks,
            deals,
            clients,
            cashflow,
            signals,
            loading,
            onTaskToggle: toggleTaskStatus,
            onTaskEdit: openTaskEditor,
            onTaskSignal: toggleTaskSignal,
            onNewTask: () => openTaskEditor(),
            onNewDeal: () => handleAction('new-deal'),
          }} />
        );
      
      // Knowledge Base view
      case 'knowledge':
        return <KnowledgeView />;
      
      // Settings view
      case 'settings':
        return <SettingsView />;
      
      // Test Suite view
      case 'test':
        return <TestView />;

      // Migration wizard (v11)
      case 'migration':
        return <MigrationView onComplete={() => setView('command')} />;

      default:
        // Check if it's a dynamic domain view
        const domainId = currentView.toUpperCase();
        const isDomainView = ['EMG', 'PRODUCT', 'GROWTH', 'OPERATIONS', 'FINANCE'].includes(domainId) ||
          currentView.startsWith('domain_');
        
        if (isDomainView) {
          return (
            <DomainView
              domain={domainId as any}
              {...domainViewProps}
            />
          );
        }
        
        return <CommandCenterView {...{
          tasks,
          deals,
          clients,
          cashflow,
          signals,
          loading,
          onTaskToggle: toggleTaskStatus,
          onTaskEdit: openTaskEditor,
          onTaskSignal: toggleTaskSignal,
          onNewTask: () => openTaskEditor(),
          onNewDeal: () => handleAction('new-deal'),
        }} />;
    }
  };
  
  // Show login if not authenticated
  if (!user) {
    return <LoginScreen onLogin={login} />;
  }
  
  // Show connection error banner if API is not connected
  const ConnectionBanner = databaseConnection.status === 'error' ? (
    <ConnectionErrorBanner
      onRetry={fetchAll}
      onSetupP2P={() => setSyncSettingsOpen(true)}
    />
  ) : null;
  
  // Show data error banner if there's an error (but connection is OK)
  const ErrorBanner = error && databaseConnection.status === 'connected' ? (
    <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-red-500">⚠️</span>
          <p className="text-red-700">{error}</p>
        </div>
        <button
          onClick={clearError}
          className="text-red-500 hover:text-red-700"
        >
          ✕
        </button>
      </div>
    </div>
  ) : null;
  
  return (
    <ErrorBoundary>
      <ToastProvider>
        <AppLayout
          currentView={currentView}
          onNavigate={handleNavigate}
          user={user}
          onLogout={logout}
          signalCount={signalCounts.critical + signalCounts.high}
          onAction={handleAction}
          selectedInitiativeId={selectedInitiativeId}
          onSelectInitiative={handleSelectInitiative}
          onAddInitiative={handleAddInitiative}
          onAddDomain={handleAddDomain}
          onDeleteDomain={handleDeleteDomain}
          onDeleteInitiative={handleDeleteInitiative}
        >
          {ConnectionBanner}
          {ErrorBanner}
          
          <Suspense fallback={<LoadingState message="Loading view..." />}>
            {renderView()}
          </Suspense>
          
          {/* Global Task Editor */}
          <TaskEditor
            task={taskEditorTask}
            isOpen={taskEditorOpen}
            onClose={closeTaskEditor}
            onSave={handleTaskSave}
            owners={owners}
          />
          
          {/* AI Chat Sidebar */}
          <ChatSidebar
            isOpen={chatOpen}
            onToggle={toggleChat}
            onCommand={handleAICommand}
            context={{ tasks, deals, signals }}
          />
          
          {/* Initiative Modal */}
          <InitiativeModal
            isOpen={initiativeModalOpen}
            onClose={() => setInitiativeModalOpen(false)}
            onSave={(data) => {
              const newInit = createInitiative(data);
              showToast(`Initiative "${newInit.name}" created`, 'success');
              setInitiativeModalOpen(false);
              handleSelectInitiative(newInit.id);
            }}
          />
          
          {/* Domain Editor Modal */}
          <DomainEditor
            isOpen={domainModalOpen}
            onClose={() => setDomainModalOpen(false)}
            onSave={(data) => {
              const newDomain = addDomain(data);
              showToast(`Domain "${newDomain.name}" created`, 'success');
              setDomainModalOpen(false);
              // Navigate to the new domain
              handleNavigate(newDomain.id.toLowerCase() as ViewId);
            }}
          />

          {/* P2P Sync Settings Modal */}
          <SyncSettingsPanel
            isOpen={syncSettingsOpen}
            onClose={() => setSyncSettingsOpen(false)}
          />
        </AppLayout>
      </ToastProvider>
    </ErrorBoundary>
  );
};

// ============================================
// EXPORT
// ============================================

export default App;
